"use client";

import {
  type Column,
  flexRender,
  type Table,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import type React from "react";
import { useEffect } from "react";

import { cn } from "@/lib/utils";
import {
  TABLE_PINNED_COLUMN,
  TABLE_ROW_BACKGROUND,
  TABLE_ROW_HOVER_BACKGROUND,
  TABLE_ROW_STRIPE_BACKGROUND,
  TABLE_STICKY_HEADER,
  TableScrollContainer,
} from "@/components/ui/table-grid";
import {
  ACTIONS_COLUMN_ID,
  columnWidthStyle,
  fluidColumnWidthStyle,
} from "./record-table-state";

const ROW_HEIGHT = 32;
const OVERSCAN_ROWS = 12;

export function RecordTableGrid<TData>({
  table,
  scrollRef,
  empty,
  renderFooter,
  hasMore = false,
  isFetchingMore = false,
  onLoadMore,
}: {
  table: Table<TData>;
  scrollRef: React.RefObject<HTMLDivElement | null>;
  empty: boolean;
  renderFooter?: (props: {
    columns: Column<TData, unknown>[];
    tableWidth: number;
  }) => React.ReactNode;
  hasMore?: boolean;
  isFetchingMore?: boolean;
  onLoadMore?: () => void;
}) {
  const rowModel = table.getRowModel();
  const virtualizer = useVirtualizer({
    count: rowModel.rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: OVERSCAN_ROWS,
  });
  const virtualRows = virtualizer.getVirtualItems();
  const tableWidth = table.getCenterTotalSize();
  const pinnedColumnClass = TABLE_PINNED_COLUMN;
  const lastVirtualIndex = virtualRows.at(-1)?.index ?? -1;

  useEffect(() => {
    if (!hasMore || isFetchingMore || !onLoadMore || rowModel.rows.length === 0) return;
    if (lastVirtualIndex >= rowModel.rows.length - 12) onLoadMore();
  }, [hasMore, isFetchingMore, lastVirtualIndex, onLoadMore, rowModel.rows.length]);

  return (
    <TableScrollContainer
      ref={scrollRef}
      className="mlx-floating-panel-scroll-surface"
    >
      <table
        className="grid w-full border-separate border-spacing-0 text-sm"
        style={{ minWidth: tableWidth }}
      >
        <thead className={cn(TABLE_STICKY_HEADER, "z-10")}>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="flex w-full text-left">
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className={cn(
                    "relative flex h-7 items-center border-b border-r border-border/50 px-2 text-xs font-medium text-muted-foreground last:border-r-0",
                    header.column.id === ACTIONS_COLUMN_ID
                      ? `${pinnedColumnClass} z-30 justify-center bg-muted px-0`
                      : "",
                  )}
                  style={
                    header.column.id === ACTIONS_COLUMN_ID
                      ? columnWidthStyle(header.getSize())
                      : fluidColumnWidthStyle(header.getSize())
                  }
                >
                  <span className="truncate">
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </span>
                  {header.column.getCanResize() ? (
                    <button
                      type="button"
                      aria-label={`Resize ${String(header.column.columnDef.header ?? "column")}`}
                      className={cn(
                        "absolute inset-y-0 -right-1 z-20 w-2 cursor-col-resize touch-none",
                        "after:absolute after:inset-y-1 after:left-1/2 after:w-px after:-translate-x-1/2 after:bg-transparent",
                        "hover:after:bg-border focus-visible:outline-none focus-visible:after:bg-ring",
                        header.column.getIsResizing() ? "after:bg-ring" : "",
                      )}
                      onMouseDown={header.getResizeHandler()}
                      onTouchStart={header.getResizeHandler()}
                    />
                  ) : null}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody
          className="relative grid"
          style={{ height: `${virtualizer.getTotalSize()}px` }}
        >
          {virtualRows.map((virtualRow) => {
            const row = rowModel.rows[virtualRow.index]!;
            return (
              <tr
                key={row.id}
                data-index={virtualRow.index}
                ref={(node) => virtualizer.measureElement(node)}
                className={cn(
                  "group/row absolute flex h-8",
                  virtualRow.index % 2 === 0
                    ? TABLE_ROW_STRIPE_BACKGROUND
                    : TABLE_ROW_BACKGROUND,
                  TABLE_ROW_HOVER_BACKGROUND,
                )}
                style={{
                  transform: `translateY(${virtualRow.start}px)`,
                  width: "100%",
                  minWidth: tableWidth,
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={cn(
                      "flex max-w-0 items-center border-r border-b border-border/40 px-2 align-middle last:border-r-0",
                      // The pinned cell inherits its row's opaque [&>td] fill, so
                      // it matches the row exactly and needs no per-cell override.
                      cell.column.id === ACTIONS_COLUMN_ID
                        ? cn(pinnedColumnClass, "justify-center px-0")
                        : "",
                    )}
                    style={
                      cell.column.id === ACTIONS_COLUMN_ID
                        ? columnWidthStyle(cell.column.getSize())
                        : fluidColumnWidthStyle(cell.column.getSize())
                    }
                  >
                    <div
                      className={cn(
                        "w-full min-w-0",
                        cell.column.id === ACTIONS_COLUMN_ID
                          ? "flex justify-center"
                          : "flex-1",
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </div>
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
        {renderFooter?.({
          columns: table.getVisibleLeafColumns(),
          tableWidth,
        })}
      </table>
      {empty ? (
        <p className="p-4 text-sm text-muted-foreground">No records yet.</p>
      ) : null}
      {!empty && (hasMore || isFetchingMore) ? (
        <div className="flex justify-center border-t border-border/40 p-3">
          <button
            type="button"
            className="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground shadow-sm hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
            onClick={onLoadMore}
            disabled={!hasMore || isFetchingMore}
          >
            {isFetchingMore ? "Loading..." : "Load more"}
          </button>
        </div>
      ) : null}
    </TableScrollContainer>
  );
}
